import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Autocomplete,
  Chip,
  Divider,
  Avatar,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Public as GlobalIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import UserProfile from "../../models/user/UserProfile";
import { currencyOptions, popularCurrencyCodes } from "../../utils/currencies";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import { ModernTextField } from "../common/StyledComponents";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const FinancialPreferencesSection: React.FC = () => {
  const theme = useTheme();
  const { setCurrency: setGlobalCurrency, formatCurrencyNumber } = useCurrencyFormatter();
  
  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { error, helpText, clearError, handleError } = useError();
  
  // Form fields
  const [currency, setCurrency] = useState("");
  const [startingBalance, setStartingBalance] = useState("0");

  const { userProfile, refetchProfile, updateProfile, isSavingProfile } = useUserProfileContext();

  useEffect(() => {
    if (userProfile) {
      setCurrency(userProfile.currency);
      setStartingBalance(userProfile.startingBalance.toString());
    }
  }, [userProfile]);

  const handleSave = () => {
    const updatedProfile: UserProfile = {
      email: userProfile?.email || "",
      name: userProfile?.name || "",
      currency,
      startingBalance: parseFloat(startingBalance) || 0
    };

    updateProfile(updatedProfile, {
      onSuccess: () => {
        setSuccess("Financial preferences updated successfully!");
        setEditing(false);
        // Update global currency immediately so the rest of the app reflects the change without refresh
        setGlobalCurrency(currency);
        refetchProfile();
      },
      onError: (error) => {
        // Use the backend's user-friendly error message
        handleError(error);
      }
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setCurrency(userProfile?.currency || "USD");
    setStartingBalance(userProfile?.startingBalance.toString() || "0");
    clearError();
  };

  const selectedCurrency = currencyOptions.find(curr => curr.code === currency);

  // Group currencies: popular first, then all others alphabetically
  const popularCurrencies = currencyOptions.filter(c => popularCurrencyCodes.includes(c.code));
  const otherCurrencies = currencyOptions
    .filter(c => !popularCurrencyCodes.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const groupedCurrencies = [
    ...popularCurrencies,
    ...otherCurrencies,
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              backgroundColor: theme.palette.success.main + '20',
              color: theme.palette.success.main,
            }}
          >
            <WalletIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Financial Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your currency and starting balance settings
            </Typography>
          </Box>
          {!editing ? (
            <Tooltip title="Edit preferences">
              <IconButton
                onClick={() => setEditing(true)}
                sx={{
                  backgroundColor: theme.palette.success.main + '10',
                  color: theme.palette.success.main,
                  '&:hover': {
                    backgroundColor: theme.palette.success.main + '20',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Save changes">
                <IconButton
                  onClick={handleSave}
                  disabled={isSavingProfile}
                  sx={{
                    backgroundColor: theme.palette.success.main + '10',
                    color: theme.palette.success.main,
                    '&:hover': {
                      backgroundColor: theme.palette.success.main + '20',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  onClick={handleCancel}
                  sx={{
                    backgroundColor: theme.palette.error.main + '10',
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: theme.palette.error.main + '20',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refetchProfile()}
        title="Financial Preferences Error"
      />

      <Grid container spacing={3}>
        {/* Main Settings Card */}
        <Grid item xs={12} md={7}>
          <Card 
            sx={{ 
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Currency Selection
              </Typography>
              
              <Autocomplete
                fullWidth
                value={currencyOptions.find(c => c.code === currency) || null}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setCurrency(newValue.code);
                  }
                }}
                options={groupedCurrencies}
                groupBy={(option) => 
                  popularCurrencyCodes.includes(option.code) ? "⭐ Popular Currencies" : "🌍 All Currencies"
                }
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                    <Typography sx={{ fontWeight: 700, minWidth: 35, fontSize: '1.1rem' }}>
                      {option.symbol}
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    <Typography color="text.secondary" sx={{ ml: 'auto' }}>
                      {option.code}
                    </Typography>
                  </Box>
                )}
                disabled={!editing}
                renderInput={(params) => (
                  <ModernTextField
                    {...params}
                    label="Currency"
                    placeholder="Search currencies..."
                    helperText={editing ? "Search or select your preferred currency" : "Click edit to change currency"}
                  />
                )}
              />

              <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.info.light + '08', borderRadius: 2, border: `1px dashed ${theme.palette.info.main}40` }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GlobalIcon sx={{ fontSize: 18 }} />
                  Currency affects all monetary displays across the application
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Settings Preview */}
        <Grid item xs={12} md={5}>
          <Card 
            sx={{ 
              border: `2px solid ${theme.palette.success.main}30`,
              backgroundColor: `linear-gradient(135deg, ${theme.palette.success.light}08 0%, ${theme.palette.success.main}08 100%)`,
              boxShadow: `0 4px 12px ${theme.palette.success.main}15`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                Current Configuration
              </Typography>
              
              {/* Currency Display */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Currency
                  </Typography>
                  <Chip 
                    label={selectedCurrency?.code || 'N/A'} 
                    size="small" 
                    sx={{ 
                      fontWeight: 600,
                      backgroundColor: theme.palette.success.main + '20',
                      color: theme.palette.success.dark
                    }} 
                  />
                </Box>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'background.paper', 
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                      {selectedCurrency?.symbol}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedCurrency?.name}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Format Example */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Display Format
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'background.paper', 
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Example
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {formatCurrencyNumber(1234.56)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3, p: 1.5, backgroundColor: theme.palette.success.main + '08', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 14 }} />
                  Changes apply immediately after saving
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Box>
  );
};

export default FinancialPreferencesSection;